import {shortcutsPath} from '@github-ui/paths'
import {useMutation} from '@github-ui/react-query'
import {verifiedFetch} from '@github-ui/verified-fetch'
import {useConfirm} from '@primer/react'

import type {SidebarLink} from '../types'
import {extractResponseErrorMessage} from '../utils/shortcut-error'

const defaultErrorMessage = 'Failed to delete shortcut'

/**
 * Hook to manage deletion of user shortcuts (saved views).
 *
 * @param onShortcutDeleted Callback invoked after a shortcut is successfully deleted.
 *
 * @returns An object containing resources and methods for managing shortcut deletion.
 */
export function useShortcutDeletion(onShortcutDeleted: (deletedShortcut: SidebarLink) => void) {
  const confirm = useConfirm()

  const deleteMutation = useMutation({
    mutationFn: async (shortcut: SidebarLink) => {
      const response = await verifiedFetch(shortcutsPath(shortcut.id), {method: 'DELETE'})
      if (!response.ok) {
        throw new Error(await extractResponseErrorMessage(response, defaultErrorMessage))
      }

      return shortcut
    },
    onSuccess: onShortcutDeleted,
  })

  /**
   * Prompts the user for confirmation and deletes the specified shortcut if confirmed.
   */
  async function deleteShortcut(shortcut: SidebarLink) {
    deleteMutation.reset()
    const performDelete = await confirm({
      title: 'Delete view',
      content: `Are you sure you want to delete view '${shortcut.name}'?`,
      confirmButtonContent: 'Delete',
      confirmButtonType: 'danger',
    })

    if (!performDelete) return

    deleteMutation.mutate(shortcut)
  }

  return {
    deleteShortcut,
    errorMessage: deleteMutation.error?.message,
    isDeleting: deleteMutation.isPending,
    reset: () => deleteMutation.reset(),
  }
}
